import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { apiClient, getErrorMessage } from '../../api/client'
import { useOfflineFirstQuery } from '../../hooks/useOfflineFirstQuery'
import { resolveTenantSlug } from '../../lib/tenant'

// ──────────────────────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────────────────────

// LIST endpoint - lean fields only
type ClientListItem = {
  id: number
  full_name: string
  date_of_birth: string
  age: number
  medical_record_number: string
  primary_diagnosis_code: string
  primary_diagnosis_description: string
}

// DETAIL endpoint - full record with all metadata + guardians + insurance
type ClientDetail = ClientListItem & {
  diagnosis_date: string | null
  diagnosing_provider_name: string | null
  diagnosing_provider_credentials: string | null
  lives_with: string | null
  allergies: string | null
  current_medications: string | null
  medical_background: string | null
  previous_interventions: string | null
  school_name: string | null
  grade_level: string | null
  iep_status: boolean
  guardians?: { id: number; first_name: string; last_name: string; relationship: string }[]
  insurance_coverages?: { id: number; payer_name: string; member_id: string; is_primary: boolean }[]
}

const schema = z.object({
  client_id: z.coerce.number().int().positive('Client is required'),
  effective_date: z.string().min(1, 'Effective date is required'),
  review_date: z.string().min(1, 'Review date is required'),
  end_date: z.string().optional().or(z.literal('')),
  presenting_concerns: z.string().min(1, 'Presenting concerns are required'),
  clinical_summary: z.string().min(1, 'Clinical summary is required'),
  treatment_recommendations: z.string().min(1, 'Treatment recommendations are required'),
  recommended_hours_per_week: z.coerce.number().min(0).max(60),
  recommended_supervision_hours: z.coerce.number().min(0).max(20),
  recommended_caregiver_training_hours: z.coerce.number().min(0).max(20),
  primary_diagnosis_code: z.string().min(1, 'Diagnosis code is required'),
  primary_diagnosis_description: z.string().min(1, 'Diagnosis description is required'),
})

type FormData = z.infer<typeof schema>

// ──────────────────────────────────────────────────────────────────────
// Section wrapper
// ──────────────────────────────────────────────────────────────────────

function Section({
  number,
  title,
  description,
  children,
}: {
  number: number
  title: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <section className="card p-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">
          <span className="inline-block w-7 h-7 rounded-full bg-primary-600 text-white text-sm leading-7 text-center mr-2">
            {number}
          </span>
          {title}
        </h2>
        {description && <p className="mt-1 text-sm text-gray-600 ml-9">{description}</p>}
      </div>
      <div className="ml-9">{children}</div>
    </section>
  )
}

function ReadOnlyField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-0.5 text-sm text-gray-900">{value || <span className="text-gray-400">—</span>}</div>
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────────────

export default function TreatmentPlanCreatePage() {
  const navigate = useNavigate()
  const tenantSlug = resolveTenantSlug()

  const [clientSearch, setClientSearch] = useState('')
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Search clients
  const clientsQuery = useOfflineFirstQuery<{ results: ClientListItem[] }>({
    queryKey: ['clients', { search: clientSearch }],
    cacheKey: `tenant:${tenantSlug ?? 'unknown'}:clients:tp_pick:q=${clientSearch.trim().toLowerCase() || 'all'}`,
    cacheMaxAgeMs: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await apiClient.get<{ results: ClientListItem[] }>('/api/v1/clients/', {
        params: clientSearch.trim() ? { search: clientSearch.trim() } : {},
      })
      return res.data
    },
  })

  const today = useMemo(() => new Date().toISOString().slice(0, 10), [])
  const sixMonthsOut = useMemo(() => {
    const d = new Date()
    d.setMonth(d.getMonth() + 6)
    return d.toISOString().slice(0, 10)
  }, [])

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      client_id: 0,
      effective_date: today,
      review_date: sixMonthsOut,
      end_date: '',
      presenting_concerns: '',
      clinical_summary: '',
      treatment_recommendations: '',
      recommended_hours_per_week: 0,
      recommended_supervision_hours: 0,
      recommended_caregiver_training_hours: 0,
      primary_diagnosis_code: '',
      primary_diagnosis_description: '',
    },
  })

  // react-hook-form gives back the raw <select> value (a string); coerce for lookups
  const clientIdNum = Number(watch('client_id')) || 0

  // Detail fetch: list endpoint is lean; we need the full record for auto-fill
  const clientDetailQuery = useOfflineFirstQuery<ClientDetail>({
    queryKey: ['client', clientIdNum, 'detail-for-tp'],
    cacheKey: `tenant:${tenantSlug ?? 'unknown'}:client:${clientIdNum}:detail`,
    cacheMaxAgeMs: 5 * 60 * 1000,
    enabled: clientIdNum > 0,
    queryFn: async () => {
      const res = await apiClient.get<ClientDetail>(`/api/v1/clients/${clientIdNum}/`)
      return res.data
    },
  })
  const selectedClient = clientDetailQuery.data ?? null

  // Auto-select on single match
  useEffect(() => {
    const r = clientsQuery.data?.results ?? []
    if (clientSearch.trim() && r.length === 1 && r[0].id !== clientIdNum) {
      setValue('client_id', r[0].id, { shouldValidate: true })
    }
  }, [clientSearch, clientsQuery.data?.results, clientIdNum, setValue])

  // Auto-fill diagnosis from selected client
  useEffect(() => {
    if (!selectedClient) return
    if (selectedClient.primary_diagnosis_code) {
      setValue('primary_diagnosis_code', selectedClient.primary_diagnosis_code)
    }
    if (selectedClient.primary_diagnosis_description) {
      setValue('primary_diagnosis_description', selectedClient.primary_diagnosis_description)
    }
  }, [selectedClient, setValue])

  const onSubmit = async (data: FormData) => {
    setSubmitError(null)
    try {
      const res = await apiClient.post<{ id: number }>('/api/v1/treatment-plans/', {
        ...data,
        client: data.client_id,
        end_date: data.end_date || null,
      })
      navigate(`/app/treatment-plans/${res.data.id}`)
    } catch (err) {
      setSubmitError(getErrorMessage(err))
    }
  }

  // ──────────────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">New Treatment Plan</h1>
        <p className="text-sm text-gray-600 mt-1">
          A guided template. Each section captures part of the plan; you can save as a draft and refine
          later. Attachments and goals can be added once the plan is saved.
        </p>
      </div>

      {submitError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {submitError}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">

        {/* Section 1: Client */}
        <Section number={1} title="Client" description="Find and confirm the client this plan is for. Their details auto-populate below.">
          <div className="space-y-4">
            <div>
              <label className="label" htmlFor="clientSearch">Find client</label>
              <input
                id="clientSearch"
                className="input mt-1"
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                placeholder="Search by name or MRN"
              />
              {clientSearch.trim() && clientsQuery.data && (
                <p className="mt-1 text-xs text-gray-500">
                  {(() => {
                    const n = (clientsQuery.data.results ?? []).length
                    if (n === 0) return 'No matches.'
                    if (n === 1) return '1 match — auto-selected below.'
                    return `${n} matches — choose from the dropdown below.`
                  })()}
                </p>
              )}
            </div>

            <div>
              <label className="label" htmlFor="client_id">Client</label>
              <select id="client_id" className="input mt-1" {...register('client_id')}>
                <option value="0">Select client</option>
                {(clientsQuery.data?.results ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.full_name} (MRN {c.medical_record_number})
                  </option>
                ))}
              </select>
              {errors.client_id && <p className="mt-1 text-sm text-red-600">{errors.client_id.message}</p>}
            </div>

            {selectedClient && (
              <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-3">Auto-populated from client record</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <ReadOnlyField label="Name" value={selectedClient.full_name} />
                  <ReadOnlyField label="DOB / age" value={`${selectedClient.date_of_birth} (age ${selectedClient.age})`} />
                  <ReadOnlyField label="MRN" value={selectedClient.medical_record_number} />
                  <ReadOnlyField
                    label="Diagnosis"
                    value={
                      selectedClient.primary_diagnosis_code
                        ? `${selectedClient.primary_diagnosis_code} — ${selectedClient.primary_diagnosis_description}`
                        : null
                    }
                  />
                  <ReadOnlyField label="Diagnosis date" value={selectedClient.diagnosis_date} />
                  <ReadOnlyField
                    label="Diagnosed by"
                    value={
                      selectedClient.diagnosing_provider_name
                        ? `${selectedClient.diagnosing_provider_name}${
                            selectedClient.diagnosing_provider_credentials
                              ? `, ${selectedClient.diagnosing_provider_credentials}`
                              : ''
                          }`
                        : null
                    }
                  />
                  <ReadOnlyField label="Lives with" value={selectedClient.lives_with} />
                  <ReadOnlyField
                    label="School / grade"
                    value={
                      selectedClient.school_name
                        ? `${selectedClient.school_name}${selectedClient.grade_level ? ` (${selectedClient.grade_level})` : ''}`
                        : null
                    }
                  />
                  <ReadOnlyField label="IEP" value={selectedClient.iep_status ? 'Yes' : 'No'} />
                  <ReadOnlyField
                    label="Guardians"
                    value={
                      selectedClient.guardians?.length
                        ? selectedClient.guardians
                            .map((g) => `${g.first_name} ${g.last_name} (${g.relationship})`)
                            .join('; ')
                        : null
                    }
                  />
                  <ReadOnlyField
                    label="Primary insurance"
                    value={selectedClient.insurance_coverages?.find((c) => c.is_primary)?.payer_name ?? null}
                  />
                </div>
              </div>
            )}
          </div>
        </Section>

        {/* Section 2: Background */}
        <Section number={2} title="Background" description="Medical, developmental, and prior-service context for this plan.">
          {selectedClient ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ReadOnlyField
                  label="Medical / developmental background"
                  value={
                    selectedClient.medical_background ? (
                      <span className="whitespace-pre-line">{selectedClient.medical_background}</span>
                    ) : null
                  }
                />
                <ReadOnlyField
                  label="Previous interventions"
                  value={
                    selectedClient.previous_interventions ? (
                      <span className="whitespace-pre-line">{selectedClient.previous_interventions}</span>
                    ) : null
                  }
                />
                <ReadOnlyField
                  label="Allergies"
                  value={
                    selectedClient.allergies ? (
                      <span className="whitespace-pre-line">{selectedClient.allergies}</span>
                    ) : null
                  }
                />
                <ReadOnlyField
                  label="Current medications"
                  value={
                    selectedClient.current_medications ? (
                      <span className="whitespace-pre-line">{selectedClient.current_medications}</span>
                    ) : null
                  }
                />
              </div>

              <div>
                <label className="label" htmlFor="presenting_concerns">Presenting concerns *</label>
                <textarea
                  id="presenting_concerns"
                  rows={4}
                  className="input mt-1"
                  placeholder="The reasons ABA services are recommended now."
                  {...register('presenting_concerns')}
                />
                {errors.presenting_concerns && (
                  <p className="mt-1 text-sm text-red-600">{errors.presenting_concerns.message}</p>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">Select a client first.</p>
          )}
        </Section>

        {/* Section 3: Diagnosis */}
        <Section number={3} title="Diagnosis" description="Primary diagnosis at time of this plan. Auto-filled from the client record; edit if this plan addresses a different diagnosis.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label" htmlFor="primary_diagnosis_code">ICD-10 code *</label>
              <input id="primary_diagnosis_code" className="input mt-1" {...register('primary_diagnosis_code')} />
              {errors.primary_diagnosis_code && (
                <p className="mt-1 text-sm text-red-600">{errors.primary_diagnosis_code.message}</p>
              )}
            </div>
            <div>
              <label className="label" htmlFor="primary_diagnosis_description">Description *</label>
              <input
                id="primary_diagnosis_description"
                className="input mt-1"
                {...register('primary_diagnosis_description')}
              />
              {errors.primary_diagnosis_description && (
                <p className="mt-1 text-sm text-red-600">{errors.primary_diagnosis_description.message}</p>
              )}
            </div>
          </div>
        </Section>

        {/* Section 4: Plan dates */}
        <Section number={4} title="Authorization period" description="Dates this plan is in effect. The review date triggers a reauthorization reminder.">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="label" htmlFor="effective_date">Effective date *</label>
              <input id="effective_date" type="date" className="input mt-1" {...register('effective_date')} />
              {errors.effective_date && (
                <p className="mt-1 text-sm text-red-600">{errors.effective_date.message}</p>
              )}
            </div>
            <div>
              <label className="label" htmlFor="review_date">Review date *</label>
              <input id="review_date" type="date" className="input mt-1" {...register('review_date')} />
              {errors.review_date && (
                <p className="mt-1 text-sm text-red-600">{errors.review_date.message}</p>
              )}
            </div>
            <div>
              <label className="label" htmlFor="end_date">End date (optional)</label>
              <input id="end_date" type="date" className="input mt-1" {...register('end_date')} />
            </div>
          </div>
        </Section>

        {/* Section 5: Service Recommendations */}
        <Section number={5} title="Service recommendations" description="Hours per week. These will be matched against insurance authorizations.">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="label" htmlFor="recommended_hours_per_week">
                Direct treatment / week (97153)
              </label>
              <input
                id="recommended_hours_per_week"
                type="number"
                step="0.5"
                min="0"
                max="60"
                className="input mt-1"
                {...register('recommended_hours_per_week')}
              />
            </div>
            <div>
              <label className="label" htmlFor="recommended_supervision_hours">
                Supervision / week (97155)
              </label>
              <input
                id="recommended_supervision_hours"
                type="number"
                step="0.5"
                min="0"
                max="20"
                className="input mt-1"
                {...register('recommended_supervision_hours')}
              />
            </div>
            <div>
              <label className="label" htmlFor="recommended_caregiver_training_hours">
                Caregiver training / week (97156)
              </label>
              <input
                id="recommended_caregiver_training_hours"
                type="number"
                step="0.5"
                min="0"
                max="20"
                className="input mt-1"
                {...register('recommended_caregiver_training_hours')}
              />
            </div>
          </div>
        </Section>

        {/* Section 6: Summary */}
        <Section number={6} title="Clinical summary" description="Synthesis of the assessment, hypothesis, and treatment direction.">
          <textarea
            rows={6}
            className="input mt-1"
            placeholder="Summary of clinical picture: presenting behaviors, suspected functions, strengths, factors affecting progress, and the rationale for the recommended intervention plan."
            {...register('clinical_summary')}
          />
          {errors.clinical_summary && (
            <p className="mt-1 text-sm text-red-600">{errors.clinical_summary.message}</p>
          )}
        </Section>

        {/* Section 7: Treatment recommendations (free-form) */}
        <Section number={7} title="Treatment recommendations" description="The intervention approach: methodologies, settings, generalization plan.">
          <textarea
            rows={6}
            className="input mt-1"
            placeholder="DTT and natural environment teaching for skill acquisition. Function-based interventions for behavior reduction. Caregiver training to support generalization. Bi-weekly BCBA supervision per BACB standards."
            {...register('treatment_recommendations')}
          />
          {errors.treatment_recommendations && (
            <p className="mt-1 text-sm text-red-600">{errors.treatment_recommendations.message}</p>
          )}
        </Section>

        {/* Sections 8 & 9: post-save placeholders */}
        <Section number={8} title="Goals & behavior targets" description="Add structured goals after saving the plan.">
          <p className="text-sm text-gray-500 italic">
            Once you save this plan as a draft, you'll be able to add long-term goals, short-term objectives,
            mastery criteria, and behavior reduction targets on the plan detail page.
          </p>
        </Section>

        <Section number={9} title="Attachments" description="Upload prior assessments, IEPs, medical records, behavior data spreadsheets, etc.">
          <p className="text-sm text-gray-500 italic">
            Attachment uploads (PDF, Word, Excel — up to 25 MB each) appear on the plan detail page once
            the plan is saved.
          </p>
        </Section>

        <Section number={10} title="Behavior data graphs" description="Visualizations of progress over time.">
          <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-sm text-gray-500">
            Behavior frequency, skill mastery, and trial-by-trial graphs will appear here once sessions are
            logged against this plan.
          </div>
        </Section>

        {/* Submit */}
        <div className="flex items-center justify-end gap-2 sticky bottom-4">
          <button type="button" className="btn-secondary" onClick={() => navigate('/app/treatment-plans')}>
            Cancel
          </button>
          <button type="submit" disabled={isSubmitting} className="btn-primary">
            {isSubmitting ? 'Saving…' : 'Save as draft'}
          </button>
        </div>
      </form>
    </div>
  )
}
